import { ReactNode, useContext } from 'react';

import { BacktestingContext } from '../../../../providers/backtesting/backtesting-provider';
import { StrategyForm } from '../strategy-form';
import { StrategySidebar } from '../strategy-sidebar';

import './styles.scss';

interface StrategyPanelProps {
  // Hide the internal strategy-switcher sidebar. Used by the Specification tab,
  // where the result shell's Overview table is the single strategy selector —
  // the internal sidebar would be a second, shadow selection mechanism.
  hideSidebar?: boolean;
  // Overrides which strategy's form is shown, instead of the context's
  // activeStrategyId. Lets the Specification tab display the stratlet selected
  // in the Overview table without mutating the shared activeStrategyId state
  // (whose lifecycle the page-level panel + sidebar depend on). `undefined`
  // means "use context activeStrategyId" — the default page-level behaviour.
  activeStrategyIdOverride?: string | null;
  // Slot rendered inside the ACTIVE form only (between Strategy Name and the
  // Account Information section) — used to place the backtest-global config
  // panel there. Rendered once (active form) rather than per mounted form.
  beforeSections?: ReactNode;
}

export const StrategyPanel = ({
  hideSidebar = false,
  activeStrategyIdOverride,
  beforeSections,
}: StrategyPanelProps) => {
  const { getActiveBacktest, activeBacktestId, getStrategyDefaults } =
    useContext(BacktestingContext);

  const activeBacktest = getActiveBacktest();
  if (!activeBacktest || !activeBacktestId) return null;

  const { strategies, activeStrategyId } = activeBacktest;
  const effectiveActiveId =
    activeStrategyIdOverride !== undefined ? activeStrategyIdOverride : activeStrategyId;

  return (
    <div className={`strategy-panel ${hideSidebar ? '' : 'strategy-panel-with-sidebar'}`}>
      {!hideSidebar && (
        <div className="strategy-panel-sidebar-container">
          <StrategySidebar />
        </div>
      )}
      <div className="strategy-panel-forms">
        {strategies.map((strategy) => {
          const isActive = strategy.id === effectiveActiveId;
          const defaults = getStrategyDefaults(activeBacktestId, strategy.id);

          return (
            <div key={strategy.id} className={`strategy-form-wrapper ${isActive ? 'visible' : ''}`}>
              <StrategyForm
                strategyId={strategy.id}
                backtestId={activeBacktestId}
                defaults={defaults}
                beforeSections={isActive ? beforeSections : undefined}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
